import { z } from "zod";

const permissive = z.any().optional();

export const ADMIN_PROMPT_MAX_LENGTH = 32000;
export const DEFAULT_PROMPT_MAX_LENGTH = 4000;
export const VERBOO_PROMPT_MAX_LENGTH = 12000;
export const DEFAULT_CUSTOM_PROMPT_MAX_LENGTH = 4500;

export const getPromptMaxLength = (isAdmin, llmProvider) => {
  if (isAdmin) {
    return ADMIN_PROMPT_MAX_LENGTH;
  }

  return llmProvider === "VERBOO" ? VERBOO_PROMPT_MAX_LENGTH : DEFAULT_PROMPT_MAX_LENGTH;
};

export const getCustomPromptMaxLength = (isAdmin) =>
  isAdmin ? ADMIN_PROMPT_MAX_LENGTH : DEFAULT_CUSTOM_PROMPT_MAX_LENGTH;

export const createEditorSchema = (isAdmin) =>
  z
    .object({
      name: z
        .string()
        .min(3, "Nome deve ter no mínimo 3 caracteres")
        .max(100, "Nome deve ter no máximo 100 caracteres"),
      language: z.string().optional(),
      isActive: z.boolean().optional(),
      isCustomPrompt: z.boolean().optional(),
      maxInteractions: isAdmin
        ? z.number().int().min(1).max(10).optional().nullable()
        : permissive,
      prompt: z.string().optional(),
      customPrompt: z.string().optional(),
      audioProvider: isAdmin ? z.string().optional() : permissive,
      audioModel: isAdmin ? z.string().optional() : permissive,
      llmProvider: z.string().optional(),
      llmModel: z.string().optional(),
      rerankerProvider: isAdmin ? z.string().optional() : permissive,
      rerankerModel: isAdmin ? z.string().optional() : permissive,
      temperature: z.number().min(0).max(2).optional(),
      topP: isAdmin ? z.number().min(0).max(1).optional() : permissive,
      restrictedByContext: z.boolean().optional(),
      maxTokens: z.number().int().optional(),
      historyLimit: isAdmin
        ? z.number().int().min(0).optional().nullable()
        : permissive,
      hasCopilot: isAdmin ? z.boolean().optional().nullable() : permissive,
      copilotProvider: isAdmin ? z.string().optional() : permissive,
      copilotModel: isAdmin ? z.string().optional() : permissive,
      copilotPrompt: isAdmin ? z.string().optional() : permissive,
      realtimeEnabled: isAdmin ? z.boolean().optional() : permissive,
      realtimeProvider: isAdmin ? z.string().optional() : permissive,
      realtimeModel: isAdmin ? z.string().optional() : permissive,
      realtimeVoice: isAdmin ? z.string().optional() : permissive,
      realtimeTranscriptionModel: isAdmin ? z.string().optional() : permissive,
      realtimeMaxDurationSeconds: isAdmin
        ? z.number().int().min(60).max(3600).optional()
        : permissive,
    })
    .superRefine((data, ctx) => {
      if (data.isCustomPrompt) {
        if (!data.customPrompt || data.customPrompt.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["customPrompt"],
            message: "O prompt personalizado é obrigatório",
          });
        }

        const maxCustomPromptLength = getCustomPromptMaxLength(isAdmin);
        if (data.customPrompt && data.customPrompt.length > maxCustomPromptLength) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["customPrompt"],
            message: `O prompt personalizado deve ter no máximo ${maxCustomPromptLength} caracteres`,
          });
        }
      } else {
        if (!data.prompt || data.prompt.trim().length < 10) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["prompt"],
            message: "O prompt deve ter mais que 10 caracteres",
          });
        }
        const maxPromptLength = getPromptMaxLength(isAdmin, data.llmProvider);
        if (data.prompt && data.prompt.length > maxPromptLength) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["prompt"],
            message: `O prompt deve ter no máximo ${maxPromptLength} caracteres`,
          });
        }
      }
    });

export const llmProviderOptions = [
  { label: "OpenAI", value: "OPENAI" },
  { label: "GROQ", value: "GROQ" },
  { label: "NVIDIA", value: "NVIDIA" },
  { label: "Google", value: "GOOGLE" },
  { label: "Deepseek", value: "DEEPSEEK" },
  { label: "Fireworks", value: "FIREWORKS" },
  { label: "OpenRouter", value: "OPENROUTER" },
  { label: "Verboo", value: "VERBOO" },
  { label: "MIMO", value: "MIMO" },
];

export const rerankerProviderOptions = [
  { label: "OpenAI", value: "OPENAI" },
  { label: "Google", value: "GOOGLE" },
  { label: "Deepseek", value: "DEEPSEEK" },
  { label: "Fireworks", value: "FIREWORKS" },
  { label: "Groq", value: "GROQ" },
  { label: "OpenRouter", value: "OPENROUTER" },
];

export const copilotProviderOptions = [
  { label: "OpenAI", value: "OPENAI" },
  { label: "Google", value: "GOOGLE" },
  { label: "Deepseek", value: "DEEPSEEK" },
];

export const languageOptions = [
  { label: "Português", value: "brazil-portuguese" },
  { label: "Inglês", value: "english" },
  { label: "Qualquer", value: "any" },
];

export const responseLimitOptions = [
  { value: 0, label: "Automático" },
  { value: 100, label: "Muito curta · ~75 palavras" },
  { value: 300, label: "Curta · ~225 palavras" },
  { value: 500, label: "Média · ~375 palavras" },
  { value: 700, label: "Longa · ~525 palavras" },
];
